import { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, type LocalVideoTrack, type RemoteVideoTrack } from 'livekit-client';
import { useAppStore } from '../store/useAppStore.js';
import { RTC_CONFIG, CALL_ROOM_OPTIONS, LOOPBACK_MIRROR_ROOM_OPTIONS } from '../lib/livekit.js';
import { requestToken } from '../api/client.js';
import { instrumentRoom } from '../debug/instrumentRoom.js';

export interface UseLoopbackResult {
  publisherRoom: Room | null;
  mirrorRoom: Room | null;
  publisherVideo: LocalVideoTrack | null;
  mirrorVideo: RemoteVideoTrack | null;
  stop(): Promise<void>;
  error: string | null;
}

export function useLoopbackRooms(): UseLoopbackResult {
  const view = useAppStore((s) => s.view);
  const userId = useAppStore((s) => s.userId);
  const setView = useAppStore((s) => s.setView);
  const setLoopbackOf = useAppStore((s) => s.setLoopbackOf);

  const [publisherRoom, setPublisherRoom] = useState<Room | null>(null);
  const [mirrorRoom, setMirrorRoom] = useState<Room | null>(null);
  const [publisherVideo, setPublisherVideo] = useState<LocalVideoTrack | null>(null);
  const [mirrorVideo, setMirrorVideo] = useState<RemoteVideoTrack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pubRef = useRef<Room | null>(null);
  const mirRef = useRef<Room | null>(null);

  const active = view === 'loopback' && !!userId;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const room = `aslgo-loopback-${userId}`;
    (async () => {
      try {
        const [pubAuth, mirrorAuth] = await Promise.all([
          requestToken({ room }),
          requestToken({ room, mirror: true }),
        ]);
        if (cancelled) return;

        const mirror = new Room(LOOPBACK_MIRROR_ROOM_OPTIONS);
        instrumentRoom(mirror, 'loopback-mirror');
        mirror.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
          // We join this room twice: the publisher as `userId`, the mirror as
          // `${userId}-mirror`. From the mirror side the publisher is the only
          // participant worth subscribing to.
          if (participant.identity !== userId) return;
          if (track.kind === Track.Kind.Video) setMirrorVideo(track as RemoteVideoTrack);
          // Audio intentionally not attached (would echo).
        });
        await mirror.connect(mirrorAuth.wsUrl, mirrorAuth.token, { rtcConfig: RTC_CONFIG });
        if (cancelled) { await mirror.disconnect(); return; }
        mirRef.current = mirror;
        setMirrorRoom(mirror);

        const publisher = new Room(CALL_ROOM_OPTIONS);
        instrumentRoom(publisher, 'loopback-publisher');
        await publisher.connect(pubAuth.wsUrl, pubAuth.token, { rtcConfig: RTC_CONFIG });
        if (cancelled) { await publisher.disconnect(); await mirror.disconnect(); return; }
        await publisher.localParticipant.enableCameraAndMicrophone();
        for (const pub of publisher.localParticipant.videoTrackPublications.values()) {
          if (pub.track) { setPublisherVideo(pub.track as LocalVideoTrack); break; }
        }
        pubRef.current = publisher;
        setPublisherRoom(publisher);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      const p = pubRef.current, m = mirRef.current;
      pubRef.current = null; mirRef.current = null;
      setPublisherRoom(null); setMirrorRoom(null); setPublisherVideo(null); setMirrorVideo(null);
      p?.disconnect().catch(() => {});
      m?.disconnect().catch(() => {});
    };
  }, [active, userId]);

  return {
    publisherRoom, mirrorRoom, publisherVideo, mirrorVideo, error,
    async stop() {
      const p = pubRef.current, m = mirRef.current;
      pubRef.current = null; mirRef.current = null;
      setLoopbackOf(null);
      setView('lobby');
      await Promise.allSettled([p?.disconnect(), m?.disconnect()]);
    },
  };
}
